import { useTheme } from '@fluentui/react';
import { Sparkline } from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPDefaultButton, IPLink, IPPivot, IPPivotItem, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import AuditTrail from 'components/Audit/AuditTrail';
import KPIDefinitionHistoryModal from 'components/KPI/KPIDefinitionHistoryModal';
import KPIDetailsForm from 'components/KPI/KPIDetailsForm';
import { KPIContext } from 'components/KPI/KPIContext';
import PreviewToggle from 'components/KPI/PreviewToggle';
import TabEvidence from 'components/KPI/Evidence/TabEvidence';
import {
  addCreatedTags,
  applyPickType,
  showTrackingCard,
  useExpressionSaveGate,
  useKPIFieldUpdaters,
  usePreviewDebounce,
} from 'components/KPI/kpiDetailsForm.helpers';
import TextInfo, { Info } from 'components/Notification/Info';
import WarningMessage from 'components/Notification/WarningMessage';
import { SaveCallback, SaveDisabledCallback } from 'components/KPI/fieldPanelTypes';
import {
  globalStackStylesPanel,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
} from 'globalStyles';
import Joi from 'joi';
import Entity, { EntityTypes } from 'models/entity';
import KPI, { KPIAggregationIntervals, KPIAggregationMethods, KPIChartTypes, KPIGraphDataConfig } from 'models/kpi/kpi';
import { KPIContextFilter } from 'models/kpi/kpiDataContext';
import KPIData from 'models/kpi/kpiData';
import Tag from 'models/tag';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiGetKPIUsage, apiGetKPIWithData } from 'services/Api/kpiService';
import { addDateMonths } from 'utils/datetime';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';

const getKPIWithData = (kpi: KPI): KPI => {
  const newKPI = kpi.clone();
  if (newKPI.data.length === 0) {
    const newData = new KPIData();
    newData.kpiId = newKPI.kpiId;
    newData.kpi = newKPI;
    newKPI.data = [newData];
  }

  return newKPI;
};

export interface IEditFieldDetailsProps {
  isOpen: boolean;
  isLoading: boolean;
  kpiToEdit: KPI;
  kpis: KPI[];
  formKpiIds?: number[];
  onDismiss: () => void;
  onEditKPI: (item: KPI) => void | Promise<void | boolean>;
  onChangeSelectedPivot?: (key: string) => void;
  updatePreviewKPI?: (kpi: KPI) => void;
  previewDismissed?: boolean;
  onTogglePreview?: () => void;
  previewToggleId?: string;
  onSaveCallback?: (callback: SaveCallback) => void;
  onSaveDisabledCallback?: (callback: SaveDisabledCallback) => void;
  taskTypeContext?: number;
  onlyIntegrationProps?: boolean;
  compactLayout?: boolean;
}

export const EditFieldDetails = (props: IEditFieldDetailsProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation([
    'translation',
    'kpi',
    'kpis',
    'forms',
    'task',
    'library',
    'theme',
    'control',
    'risk',
    'auditTrail',
  ]);
  const entityTerm = (isField: boolean): string => t(isField ? 'kpis:Entity.Field' : 'kpis:Entity.KPI');
  const [error, setError] = useState<Joi.ValidationError | undefined>(undefined);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [selectedPivotKey, setSelectedPivotKey] = useState<string>('details');
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [kpi, setKPI] = useState<KPI>(getKPIWithData(props.kpiToEdit));
  const [kpiTags, setKPITags] = useState<Tag[]>([]);
  const [kpiUsage, setKPIUsage] = useState<Entity[]>([]);
  const [showUsageForms, setShowUsageForms] = useState<boolean>(false);
  const [trend, setTrend] = useState<number[] | undefined>(undefined);
  const theme = useTheme();
  const history = useHistory();
  const nameCheckSeq = useRef(0);
  const nameCheckTimer = useRef<number | undefined>(undefined);

  const showTypeConfig = !props.onlyIntegrationProps;
  const showInstruction = !props.onlyIntegrationProps;
  const showExpectedResult = !props.onlyIntegrationProps;
  const showAttachments = !props.onlyIntegrationProps;
  const showPermissions = !props.onlyIntegrationProps;
  const initialAccordion = props.onlyIntegrationProps ? 2 : undefined;

  const fieldUpdaters = useKPIFieldUpdaters({
    kpi,
    onChange: setKPI,
    tags: kpiTags,
    onTagsChange: setKPITags,
  });

  usePreviewDebounce(kpi, props.isOpen, props.isLoading, props.updatePreviewKPI);

  const expressionGate = useExpressionSaveGate(kpi, props.kpiToEdit);

  useEffect(() => {
    if (props.isOpen) {
      setError(undefined);
      setNameError(undefined);
      expressionGate.reset();
      setSelectedPivotKey('details');
      setShowUsageForms(false);
      setKPI(getKPIWithData(props.kpiToEdit));
      setKPITags(appContext.globalDataCache.tags.getItemsForId(props.kpiToEdit.tagIds));
      loadContext(props.kpiToEdit.kpiId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.kpiToEdit]);

  useEffect(() => {
    let cancelled = false;
    const loadTrend = async () => {
      if (!props.isOpen || props.onlyIntegrationProps || !props.kpiToEdit.kpiId || props.kpiToEdit.kpiId <= 0) {
        setTrend(undefined);

        return;
      }
      try {
        const config = new KPIGraphDataConfig();
        config.kpiId = props.kpiToEdit.kpiId;
        config.chartType = KPIChartTypes.Line;
        config.aggregationMethod = KPIAggregationMethods.Count;
        config.aggregationInterval = KPIAggregationIntervals.Month;
        config.periodStart = addDateMonths(new Date(), -12);
        config.periodEnd = new Date();
        config.contextFilter = new KPIContextFilter();
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const withData = await apiGetKPIWithData(
          props.kpiToEdit.kpiId,
          config,
          accessToken,
          appContext.globalDataCache,
        );
        if (cancelled) return;
        const points = withData?.graphData?.points?.map((p) => p.value ?? 0) ?? [];
        setTrend(points.length >= 2 ? points : undefined);
      } catch {
        if (!cancelled) setTrend(undefined);
      }
    };
    loadTrend();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.kpiToEdit.kpiId, props.onlyIntegrationProps]);

  useEffect(() => {
    if (props.onSaveCallback) props.onSaveCallback(onSave);
    if (props.onSaveDisabledCallback) props.onSaveDisabledCallback(disableSave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpi, kpiTags, error, nameError, expressionGate.state]);

  const loadContext = async (kpiId: number) => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const usage = await apiGetKPIUsage(kpiId, accessToken);
      // The server reports every form row that references this KPI, and by design that includes the
      // hidden per-task copies of a form: separate ids carrying the same name. The user sees one form,
      // so the name is the identity here - it keeps the count honest and the list free of repeats.
      // The form being edited is dropped by name too, or its hidden copies come back as "other forms".
      const formUsage = usage.filter((u) => u.typeOfEntity === EntityTypes.TaskType);
      const contextName = formUsage.find((u) => u.entityId === props.taskTypeContext)?.entityName;
      const otherForms = formUsage.filter(
        (u, index) =>
          u.entityName !== contextName && formUsage.findIndex((f) => f.entityName === u.entityName) === index,
      );

      if (props.taskTypeContext) {
        setKPIUsage(otherForms);
      } else {
        setKPIUsage(otherForms.length > 1 ? otherForms : []);
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  const onChangeSelectedPivot = (key: string) => {
    setSelectedPivotKey(key);
    if (props.onChangeSelectedPivot) props.onChangeSelectedPivot(key);
  };

  const _localizedFields: Record<string, string> = {
    name: t('kpis:Fields.Name'),
    defValue: t('kpis:Fields.Question'),
  };

  const getFieldError = (fieldName: string): string | undefined => {
    if (nameError) return nameError;

    return error?.details?.find((e) => e.context?.key === fieldName)?.message;
  };

  const validate = async (fieldName: string, nextKpi: KPI): Promise<string> => {
    const result = nextKpi.validate(_localizedFields);
    setError(result.error);

    if (result.error) {
      const fieldError = result.error.details.find((e) => e.context?.key === fieldName);
      if (fieldError) return fieldError.message;
    }

    if (fieldName === 'name') {
      // Newest check wins: cancel the pending server check and invalidate in-flight responses,
      // so a stale "no duplicate" can never clear a real error.
      if (nameCheckTimer.current) window.clearTimeout(nameCheckTimer.current);
      const seq = ++nameCheckSeq.current;
      const typed = nextKpi.name.trim().toLowerCase();

      if (!typed) {
        setNameError(undefined);

        return '';
      }

      // Instant verdict from the loaded pool; no roundtrip for the common case.
      const localMatch = props.kpis.find((k) => k.kpiId !== nextKpi.kpiId && k.name.trim().toLowerCase() === typed);
      if (localMatch) {
        setNameError(t('kpis:Errors.NameError', { entity: entityTerm(nextKpi.isField) }));

        return '';
      }

      // The server is only asked about names outside the loaded pool (hidden or restricted),
      // once per typing pause instead of once per keystroke.
      nameCheckTimer.current = window.setTimeout(async () => {
        try {
          const existingId = await KPI.nameExists(nextKpi.name.toLowerCase(), appContext);
          if (seq !== nameCheckSeq.current) return;

          // The server matches more loosely than the local compare (collation), so a hit can
          // still be in the loaded pool; only a truly invisible name gets the hidden message.
          if (existingId && existingId !== nextKpi.kpiId && props.kpis.some((k) => k.kpiId === existingId)) {
            setNameError(t('kpis:Errors.NameError', { entity: entityTerm(nextKpi.isField) }));
          } else if (existingId && existingId !== nextKpi.kpiId) {
            setNameError(t('kpis:Errors.NameErrorHiddenKPI', { entity: entityTerm(nextKpi.isField) }));
          } else {
            setNameError(undefined);
          }
        } catch {
          // A failed check keeps the current verdict; the server still rejects real duplicates.
        }
      }, 400);
    }

    return '';
  };

  const hasChanged = (): boolean => !kpi.isEqual(props.kpiToEdit);

  // The dirty-check and the onChange-driven error states are not enough on their own: the API's
  // rules (name required, SuccesError question required without label) must hold for the whole
  // draft on every gate evaluation, or an edit elsewhere on the form can submit an invalid KPI.
  const hasError = (): boolean => !!kpi.validate(_localizedFields).error;

  const disableSave = (): boolean =>
    !hasChanged() || hasError() || !!error || !!nameError || expressionGate.blocksSave || props.isLoading;

  const onSave = async () => {
    const validationResult = kpi.validate(_localizedFields);
    if (validationResult.error) {
      setError(validationResult.error);

      return false;
    }
    const createdTags = await addCreatedTags(kpiTags, appContext);
    const allTags = kpiTags.filter((a) => a.tagId >= 0);
    allTags.push(...createdTags);
    kpi.tagIds = allTags.map((tag) => tag.tagId);

    return await props.onEditKPI(kpi);
  };

  const onConvert = async (toField: boolean) => {
    const createdTags = await addCreatedTags(kpiTags, appContext);
    const allTags = kpiTags.filter((a) => a.tagId >= 0);
    allTags.push(...createdTags);
    const newKPI = kpi.clone();
    newKPI.tagIds = allTags.map((tag) => tag.tagId);
    newKPI.isField = toField;
    // Only report the conversion once the host has accepted it: a refused save (an error, or a
    // definition change waiting on confirmation) leaves the panel open instead of announcing a
    // change that did not happen.
    const accepted = await props.onEditKPI(newKPI);
    if (accepted === false) return;

    if (!toField) {
      const coachParts = t('kpis:Tracking.FirstConvertCoach').split('%LINK%');
      toast.success(
        <span>
          {coachParts[0]}
          <IPLink onClick={() => history.push('/kpis/overview')}>{t('kpis:Tracking.FirstConvertCoachLink')}</IPLink>
          {coachParts[1] ?? ''}
        </span>,
        { containerId: 'app', autoClose: 8000 },
      );
    } else {
      toast.success(t('kpis:Tracking.Demoted'), { containerId: 'app' });
    }
    props.onDismiss();
  };

  const stackStyles = props.compactLayout ? { root: { paddingTop: 0, paddingBottom: 0 } } : globalStackStylesPanel;
  const stackTokens = props.compactLayout ? globalStackTokensGapSmall : globalStackTokensGapMedium;

  const renderTrackingCard = () => {
    if (kpi.kpiId <= 0 || props.onlyIntegrationProps) return null;

    // Integration-managed KPIs are marked IsSystem on the backend and must stay KPIs; the server
    // rejects converting them to a field. Read isSystem from the original prop because KPI.clone()
    // (used for the edit draft) does not copy it.
    const isManaged = props.kpiToEdit.isSystem;

    if (!showTrackingCard(kpi.isField, isManaged, !!props.taskTypeContext)) return null;

    return (
      <IPStack
        tokens={globalStackTokensGapSmall}
        styles={{
          root: {
            marginTop: 10,
          },
        }}
      >
        {kpi.isField ? (
          <Fragment>
            {trend && (
              <Sparkline
                data={{
                  chartTitle: t('kpis:Tracking.TrendTitle'),
                  lineChartData: [
                    { legend: '', color: theme.palette.themePrimary, data: trend.map((y, x) => ({ x: x + 1, y })) },
                  ],
                }}
                width={160}
                height={36}
                showLegend={false}
              />
            )}
            <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
              <IPDefaultButton
                iconProps={{ iconName: 'LineChart' }}
                text={t('kpis:Tracking.TrackOverTime')}
                onClick={() => onConvert(false)}
              />
              <Info message={t('kpis:Tracking.TrackBenefit')} />
            </IPStack>
          </Fragment>
        ) : (
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPStack horizontal wrap verticalAlign="center" tokens={globalStackTokensGapSmall}>
              {/* Keep the label and the link in ONE IPText so the link is part of the same inline
                  text run: it inherits the exact font size and baseline of the label (a standalone
                  Fluent Link sizes off the panel and renders taller). Two non-breaking spaces set the
                  gap (the browser collapses regular spaces down to one). */}
              <IPText styles={{ root: { color: theme.palette.neutralSecondary } }}>
                {t('kpis:Tracking.Tracked')}
                {!isManaged && (
                  <>
                    {'  '}
                    <IPLink onClick={() => onConvert(true)}>{t('kpis:Tracking.ConvertToField')}</IPLink>
                  </>
                )}
              </IPText>
              {!isManaged && <Info message={t('kpis:Tracking.ConvertBenefit')} />}
            </IPStack>
          </IPStack>
        )}
      </IPStack>
    );
  };

  return (
    <IPStack id="kpiPreviewTarget" verticalFill styles={stackStyles}>
      {props.onTogglePreview && (
        <PreviewToggle
          previewDismissed={props.previewDismissed}
          onTogglePreview={props.onTogglePreview}
          previewToggleId={props.previewToggleId}
        />
      )}
      <IPPivot
        selectedKey={selectedPivotKey}
        onLinkClick={(item) => onChangeSelectedPivot(item?.props.itemKey ?? 'details')}
        headersOnly={true}
      >
        <IPPivotItem key="details" itemKey="details" headerText={t('kpi:Pivot.Definition')} />
        {!kpi.isField && <IPPivotItem key="context" itemKey="context" headerText={t('kpi:Pivot.Context')} />}
        {!kpi.isField &&
          !props.onlyIntegrationProps &&
          kpi.isEvidenceType() &&
          hasUserFeature(appContext, FeatureTypes.Automation) && (
            <IPPivotItem key="evidence" itemKey="evidence" headerText={t('kpi:Pivot.Evidence')} />
          )}
        {!kpi.isField && <IPPivotItem key="changelog" itemKey="changelog" headerText={t('kpi:Pivot.ChangeLog')} />}
      </IPPivot>
      {selectedPivotKey === 'details' && (
        <IPStack verticalFill styles={stackStyles} tokens={stackTokens}>
          {/* The forms themselves are named on demand rather than in a hover tooltip: the list is the
              first thing the reader wants after the warning, and a link says so where an info icon
              only hints at it. The link is part of the same inline text run (two non-breaking spaces
              set the gap, since the browser collapses regular ones) so it trails the sentence instead
              of claiming a line of its own. */}
          {kpiUsage.length > 0 && (
            <IPStack.Item>
              <WarningMessage>
                <IPStack tokens={globalStackTokensGapExtraSmall}>
                  <IPStack.Item>
                    <IPText>
                      {props.taskTypeContext
                        ? t('kpis:Panels.Edit.KPIUsageWarningOther', {
                            count: kpiUsage.length,
                            entity: entityTerm(kpi.isField),
                          })
                        : t('kpis:Panels.Edit.KPIUsageWarning', {
                            count: kpiUsage.length,
                            entity: entityTerm(kpi.isField),
                          })}
                      {'  '}
                      <IPLink onClick={() => setShowUsageForms(!showUsageForms)}>
                        {showUsageForms
                          ? t('kpis:Panels.Edit.KPIUsageHideForms')
                          : t('kpis:Panels.Edit.KPIUsageShowForms')}
                      </IPLink>
                    </IPText>
                  </IPStack.Item>
                  {showUsageForms && (
                    <IPStack.Item>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {kpiUsage.map((u) => (
                          <li key={u.entityId}>
                            <IPText>{u.entityName}</IPText>
                          </li>
                        ))}
                      </ul>
                    </IPStack.Item>
                  )}
                </IPStack>
              </WarningMessage>
            </IPStack.Item>
          )}
          {/* One line about what editing this definition does, with the consequence for answers already
              recorded behind the info icon: the rule matters here, the detail only when asked for. The
              text never changes while editing - a message that rewrites itself on the first keystroke
              reads as an error. On the same line, right-aligned, the definition's own version and the
              way into its history: both facts belong to the fields below that cause a new version. */}
          {(!props.onlyIntegrationProps || !!props.kpiToEdit.currentDefinitionRevisionNumber) && (
            <IPStack.Item>
              <IPStack
                horizontal
                horizontalAlign="space-between"
                verticalAlign="center"
                tokens={globalStackTokensGapSmall}
              >
                <IPStack.Item grow>
                  {!props.onlyIntegrationProps && (
                    <TextInfo message={t('forms:Versioning.KPIDefinitionAnswers')}>
                      <IPText variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                        {t('forms:Versioning.KPIDefinitionInfo')}
                      </IPText>
                    </TextInfo>
                  )}
                </IPStack.Item>
                {!!props.kpiToEdit.currentDefinitionRevisionNumber && (
                  <IPText variant="small" styles={{ root: { whiteSpace: 'nowrap' } }}>
                    <IPLink onClick={() => setShowHistory(true)}>
                      {t('forms:Versioning.Version', { version: props.kpiToEdit.currentDefinitionRevisionNumber })}
                    </IPLink>
                  </IPText>
                )}
              </IPStack>
            </IPStack.Item>
          )}
          <KPIDetailsForm
            kpi={kpi}
            kpis={props.kpis}
            formKpiIds={props.formKpiIds}
            originalExpression={expressionGate.originalExpression}
            expressionValidationUpdated={expressionGate.onValidationStateChange}
            mode="edit"
            entityTerm={entityTerm}
            onChange={setKPI}
            restrictTypeChanges={true}
            onPickType={(type) => {
              expressionGate.reset();
              setKPI(applyPickType(kpi, type, t));
            }}
            {...fieldUpdaters}
            tags={kpiTags}
            nameInfoMessage={t('kpis:Fields.NameInfo', { entity: entityTerm(kpi.isField) })}
            fieldError={getFieldError}
            onNameChange={(nextKpi) => {
              validate('name', nextKpi);
              setKPI(nextKpi);
            }}
            showTypeConfig={showTypeConfig}
            showInstruction={showInstruction}
            showExpectedResult={showExpectedResult}
            showAttachments={showAttachments}
            showPermissions={showPermissions}
            initialAccordion={initialAccordion}
            compactLayout={props.compactLayout}
            renderAfterName={() => {
              const card = renderTrackingCard();
              if (!card) return null;

              return <IPStack.Item>{card}</IPStack.Item>;
            }}
          />
        </IPStack>
      )}
      {selectedPivotKey === 'evidence' && (
        <IPStack verticalFill styles={globalStackStylesPanel} tokens={globalStackTokensGapMedium}>
          <TabEvidence selectedKPI={kpi} isBusy={() => false} isLoading={props.isLoading} />
        </IPStack>
      )}
      {selectedPivotKey === 'context' && (
        <IPStack verticalFill styles={globalStackStylesPanel} tokens={globalStackTokensGapMedium}>
          <KPIContext kpi={kpi} />
        </IPStack>
      )}
      {selectedPivotKey === 'changelog' && (
        <IPStack verticalFill styles={globalStackStylesPanel} tokens={globalStackTokensGapMedium}>
          <AuditTrail auditTrailId={kpi.auditTrailId} />
        </IPStack>
      )}
      {showHistory && (
        <KPIDefinitionHistoryModal kpi={props.kpiToEdit} isOpen={showHistory} onDismiss={() => setShowHistory(false)} />
      )}
    </IPStack>
  );
};
